import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Space,
  Typography,
  Card,
  Divider,
  Tag,
  message,
  Input,
  DatePicker,
} from 'antd';
import {
  FileExcelOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import type { ColumnType } from 'antd/es/table';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import CustomerResponse from '../../model/Customer.model';
import {
  formatCardNumber,
  formatCurrency,
  getTotalDepositAmount,
  getTotalWithdrawalAmount,
  getBalanceDifference,
  getStatusText,
  getStatusColor,
} from './MaturityTableUtils';
import { exportMultipleMaturitySchedulesToExcel } from '../../utils/excelExport';
import CustomerService from '../../service/customer-service/Customer.service';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

interface MultiExportModalProps {
  visible: boolean;
  onClose: () => void;
  maturitySchedules: MaturityScheduleResponse[];
  selectedMonth: number;
  selectedYear: number;
}

// Extended interface to include customer details with partner info
interface EnrichedMaturitySchedule extends MaturityScheduleResponse {
  customerDetails?: CustomerResponse;
}

const MultiExportModal: React.FC<MultiExportModalProps> = ({
  visible,
  onClose,
  maturitySchedules,
  selectedMonth,
  selectedYear,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [enrichedData, setEnrichedData] = useState<EnrichedMaturitySchedule[]>(
    []
  );
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Search state - remove main search, keep for column filters
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<any>(null);

  // Add table key to force re-render when modal opens
  const [tableKey, setTableKey] = useState(0);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<Record<string, any>>({});

  // Ref to track current filtered data
  const tableRef = useRef<any>(null);
  const [currentFilteredData, setCurrentFilteredData] = useState<
    EnrichedMaturitySchedule[]
  >([]);

  // Fetch customer details when modal opens or data changes
  useEffect(() => {
    if (visible && maturitySchedules.length > 0) {
      fetchCustomerDetails();
      // Reset table key to force re-render and clear filters
      setTableKey((prev) => prev + 1);
      setSelectedRowKeys([]);
      setFilteredInfo({});
      setSortedInfo({});
    }
  }, [visible, maturitySchedules]);

  const fetchCustomerDetails = async () => {
    setLoadingCustomers(true);
    try {
      // Get unique customer IDs
      const customerIds = Array.from(
        new Set(
          maturitySchedules
            .map((ms) => ms.creditCard?.customer?.id)
            .filter(Boolean)
        )
      );

      // Fetch customer details in parallel
      const customerPromises = customerIds.map((id) =>
        CustomerService.getCustomerById(id).catch((error) => {
          console.error(`Error fetching customer ${id}:`, error);
          return null;
        })
      );

      const customers = await Promise.all(customerPromises);
      const customerMap = new Map();

      customers.forEach((customer) => {
        if (customer) {
          customerMap.set(customer.id, customer);
        }
      });

      // Enrich maturity schedules with customer details
      const enriched = maturitySchedules.map((ms) => ({
        ...ms,
        customerDetails: customerMap.get(ms.creditCard?.customer?.id),
      }));

      setEnrichedData(enriched);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      message.error('Có lỗi xảy ra khi tải thông tin khách hàng');
      setEnrichedData(maturitySchedules);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Filter out records with no transactions or zero amounts, and exclude ABANDONED status
  const validMaturitySchedules = useMemo(() => {
    return enrichedData.filter((record) => {
      // Exclude ABANDONED status
      if (record.status === 'ABANDONED') {
        return false;
      }

      const totalDeposit = getTotalDepositAmount(record);
      const totalWithdrawal = getTotalWithdrawalAmount(record);
      return (
        totalDeposit > 0 || totalWithdrawal > 0 || record.repaymentAmount > 0
      );
    });
  }, [enrichedData]);

  // Update filtered data when valid maturity schedules change
  useEffect(() => {
    setCurrentFilteredData(validMaturitySchedules);
  }, [validMaturitySchedules]);

  // Get unique bank options for filter
  const bankOptions = useMemo(() => {
    const banks = Array.from(
      new Set(
        validMaturitySchedules
          .map((record) => record.creditCard?.bank?.name)
          .filter(Boolean)
      )
    );
    return banks.map((bank) => ({
      text: bank as string,
      value: bank as string,
    }));
  }, [validMaturitySchedules]);

  // Get unique partner options for filter
  const partnerOptions = useMemo(() => {
    const partners = Array.from(
      new Set(
        validMaturitySchedules
          .map((record) => record.customerDetails?.partnerName)
          .filter(Boolean)
      )
    );
    return partners.map((partner) => ({
      text: partner as string,
      value: partner as string,
    }));
  }, [validMaturitySchedules]);

  // Get date range for current month
  const currentMonthRange = useMemo(() => {
    const startOfMonth = dayjs()
      .year(selectedYear)
      .month(selectedMonth - 1)
      .startOf('month');
    const endOfMonth = dayjs()
      .year(selectedYear)
      .month(selectedMonth - 1)
      .endOf('month');
    return [startOfMonth, endOfMonth];
  }, [selectedMonth, selectedYear]);

  // Handle table change to track filtered data
  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);

    // Update current filtered data for all actions
    if (extra.currentDataSource) {
      setCurrentFilteredData(extra.currentDataSource);
    }
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setCurrentFilteredData(validMaturitySchedules);
    // Không xóa selectedRowKeys - giữ nguyên selections
    setTableKey((prev) => prev + 1); // Force table re-render
    message.success('Đã xóa tất cả bộ lọc');
  };

  // Search functionality for columns
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (
    dataIndex: string,
    placeholder: string
  ): ColumnType<EnrichedMaturitySchedule> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filteredValue: filteredInfo[dataIndex] || null,
    onFilter: (value, record) => {
      let searchValue = '';

      switch (dataIndex) {
        case 'cardInfo':
          const cardLast4 = record.creditCard?.cardNumber?.slice(-4) || '';
          const cardHolderName = record.creditCard?.cardHolderName || '';
          const customerName = record.creditCard?.customer?.name || '';
          searchValue = `${cardLast4} ${cardHolderName} ${customerName}`;
          break;
        case 'repaymentAmount':
          searchValue = record.repaymentAmount.toString();
          break;
        case 'depositAmount':
          searchValue = getTotalDepositAmount(record).toString();
          break;
        case 'withdrawalAmount':
          searchValue = getTotalWithdrawalAmount(record).toString();
          break;
        case 'feeApplied':
          searchValue = (record.feeApplied || 0).toString();
          break;
        case 'partnerInfo':
          const partnerName = record.customerDetails?.partnerName || '';
          const partnerCode = record.customerDetails?.partnerCode || '';
          searchValue = `${partnerName} ${partnerCode}`;
          break;
        case 'commissionRate':
          searchValue = (
            record.customerDetails?.commissionRateForPartner || 0
          ).toString();
          break;
        default:
          searchValue = '';
      }

      return searchValue
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // Date filter for due date column
  const getDateFilterProps = (): ColumnType<EnrichedMaturitySchedule> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <RangePicker
          value={
            selectedKeys[0]
              ? JSON.parse(selectedKeys[0] as string).map((date: string) =>
                  dayjs(date)
                )
              : null
          }
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setSelectedKeys([
                JSON.stringify([
                  dates[0].format('YYYY-MM-DD'),
                  dates[1].format('YYYY-MM-DD'),
                ]),
              ]);
            } else {
              setSelectedKeys([]);
            }
          }}
          disabledDate={(current) => {
            const [startOfMonth, endOfMonth] = currentMonthRange;
            return (
              current &&
              (current.isBefore(startOfMonth, 'day') ||
                current.isAfter(endOfMonth, 'day'))
            );
          }}
          style={{ marginBottom: 8, display: 'block' }}
          placeholder={['Từ ngày', 'Đến ngày']}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Lọc
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filteredValue: filteredInfo.dueDate || null,
    onFilter: (value, record) => {
      if (!value) return true;

      const [startDate, endDate] = JSON.parse(value as string);
      const recordDate = dayjs(record.dueDate);

      return (
        recordDate.isAfter(dayjs(startDate).subtract(1, 'day')) &&
        recordDate.isBefore(dayjs(endDate).add(1, 'day'))
      );
    },
  });

  // Calculate preview totals for selected records
  const previewData = useMemo(() => {
    const selectedRecords = validMaturitySchedules.filter((record) =>
      selectedRowKeys.includes(record.id)
    );

    const totals = selectedRecords.reduce(
      (acc, record) => {
        const deposit = getTotalDepositAmount(record);
        const withdrawal = getTotalWithdrawalAmount(record);
        const fee = (record.repaymentAmount * (record.feeApplied || 0)) / 100;
        const ctvCommissionRate =
          record.customerDetails?.commissionRateForPartner || 0;
        const ctvCommission = (ctvCommissionRate * withdrawal) / 100;

        // Calculate HH (Hoa hồng) = |phí thu khách% - phí ctv%| * rút
        const marginRate = Math.abs(
          (record.feeApplied || 0) - ctvCommissionRate
        );
        const margin = (marginRate * withdrawal) / 100;

        const balance = getBalanceDifference(record);

        return {
          count: acc.count + 1,
          totalRepayment: acc.totalRepayment + record.repaymentAmount,
          totalDeposit: acc.totalDeposit + deposit,
          totalWithdrawal: acc.totalWithdrawal + withdrawal,
          totalFee: acc.totalFee + fee,
          totalCtvCommission: acc.totalCtvCommission + ctvCommission,
          totalMargin: acc.totalMargin + margin,
          totalBalance: acc.totalBalance + balance,
        };
      },
      {
        count: 0,
        totalRepayment: 0,
        totalDeposit: 0,
        totalWithdrawal: 0,
        totalFee: 0,
        totalCtvCommission: 0,
        totalMargin: 0,
        totalBalance: 0,
      }
    );

    return totals;
  }, [validMaturitySchedules, selectedRowKeys]);

  const columns = [
    {
      title: 'Chọn',
      width: 60,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record.id]);
            } else {
              setSelectedRowKeys(
                selectedRowKeys.filter((key) => key !== record.id)
              );
            }
          }}
        />
      ),
    },
    {
      title: 'Thông tin thẻ',
      key: 'cardInfo',
      dataIndex: 'cardInfo',
      width: 200,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <div className="space-y-1">
          <div className="font-medium text-blue-600 text-sm">
            {formatCardNumber(record.creditCard?.cardNumber)}
          </div>
          <div className="text-sm text-gray-800">
            <span className="text-gray-500">Chủ thẻ: </span>
            <span className="font-medium">
              {record.creditCard?.cardHolderName || 'N/A'}
            </span>
          </div>
          <div className="text-sm text-gray-800">
            <span className="text-gray-500">Khách hàng: </span>
            <span className="font-medium text-green-600">
              {record.creditCard?.customer?.name || 'N/A'}
            </span>
          </div>
        </div>
      ),
      ...getColumnSearchProps(
        'cardInfo',
        'Tìm theo số thẻ, chủ thẻ, khách hàng...'
      ),
    },
    {
      title: 'Ngân hàng',
      key: 'bank',
      dataIndex: 'bank',
      width: 140,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <div className="text-sm">
          <div className="font-medium">
            {record.creditCard?.bank?.name || 'N/A'}
          </div>
          <Tag color="gray">{record.creditCard?.cardType?.name || 'N/A'}</Tag>
        </div>
      ),
      filters: bankOptions,
      onFilter: (value: any, record: EnrichedMaturitySchedule) =>
        record.creditCard?.bank?.name === value,
      filteredValue: filteredInfo.bank || null,
    },

    {
      title: 'Số tiền đáo hạn',
      key: 'repaymentAmount',
      dataIndex: 'repaymentAmount',
      width: 130,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(record.repaymentAmount)}
        </span>
      ),
      sorter: (a: EnrichedMaturitySchedule, b: EnrichedMaturitySchedule) =>
        a.repaymentAmount - b.repaymentAmount,
      ...getColumnSearchProps('repaymentAmount', 'Tìm theo số tiền...'),
    },
    {
      title: 'Đã nạp',
      key: 'depositAmount',
      dataIndex: 'depositAmount',
      width: 120,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(getTotalDepositAmount(record))}
        </span>
      ),
      sorter: (a: EnrichedMaturitySchedule, b: EnrichedMaturitySchedule) =>
        getTotalDepositAmount(a) - getTotalDepositAmount(b),
      ...getColumnSearchProps('depositAmount', 'Tìm theo số tiền đã nạp...'),
    },
    {
      title: 'Đã rút',
      key: 'withdrawalAmount',
      dataIndex: 'withdrawalAmount',
      width: 120,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className="font-semibold text-orange-600">
          {formatCurrency(getTotalWithdrawalAmount(record))}
        </span>
      ),
      sorter: (a: EnrichedMaturitySchedule, b: EnrichedMaturitySchedule) =>
        getTotalWithdrawalAmount(a) - getTotalWithdrawalAmount(b),
      ...getColumnSearchProps('withdrawalAmount', 'Tìm theo số tiền đã rút...'),
    },
    {
      title: 'Phí (%)',
      key: 'feeApplied',
      dataIndex: 'feeApplied',
      width: 80,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span>{record.feeApplied ? `${record.feeApplied}%` : '-'}</span>
      ),
      sorter: (a: EnrichedMaturitySchedule, b: EnrichedMaturitySchedule) =>
        (a.feeApplied || 0) - (b.feeApplied || 0),
      ...getColumnSearchProps('feeApplied', 'Tìm theo tỷ lệ phí...'),
    },

    {
      title: 'Phí CTV (%)',
      key: 'commissionRate',
      dataIndex: 'commissionRate',
      width: 100,
      render: (_: any, record: EnrichedMaturitySchedule) => {
        const commissionRate = record.customerDetails?.commissionRateForPartner;
        return (
          <span className="text-sm font-medium text-purple-600">
            {commissionRate ? `${commissionRate}%` : '-'}
          </span>
        );
      },
      sorter: (a: EnrichedMaturitySchedule, b: EnrichedMaturitySchedule) =>
        (a.customerDetails?.commissionRateForPartner || 0) -
        (b.customerDetails?.commissionRateForPartner || 0),
      ...getColumnSearchProps('commissionRate', 'Tìm theo phí CTV...'),
    },
    {
      title: 'CTV',
      key: 'partnerInfo',
      dataIndex: 'partnerInfo',
      width: 120,
      render: (_: any, record: EnrichedMaturitySchedule) => {
        const partnerName = record.customerDetails?.partnerName;
        const partnerCode = record.customerDetails?.partnerCode;

        if (partnerName) {
          return (
            <div className="text-sm">
              <div className="font-medium text-purple-600">{partnerName}</div>
              {partnerCode && (
                <div className="text-xs text-gray-500">({partnerCode})</div>
              )}
            </div>
          );
        } else if (partnerCode) {
          return (
            <span className="text-sm font-medium text-purple-600">
              {partnerCode}
            </span>
          );
        } else {
          return <span className="text-sm text-gray-400">Chưa có CTV</span>;
        }
      },
      filters: partnerOptions,
      filterMultiple: true,
      filterSearch: true,
      onFilter: (values: any, record: EnrichedMaturitySchedule) => {
        const selected = Array.isArray(values) ? values : [values];
        return selected.includes(record.customerDetails?.partnerName);
      },
      filteredValue: filteredInfo.partnerInfo || null,
      // XÓA dòng ...getColumnSearchProps('partnerInfo', ...)
    },
    {
      title: 'Hạn đáo',
      key: 'dueDate',
      dataIndex: 'dueDate',
      width: 100,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span>{dayjs(record.dueDate).format('DD/MM/YYYY')}</span>
      ),
      sorter: (a: EnrichedMaturitySchedule, b: EnrichedMaturitySchedule) =>
        dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      ...getDateFilterProps(),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      dataIndex: 'status',
      width: 100,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className={getStatusColor(record.status)}>
          {getStatusText(record.status)}
        </span>
      ),
      filters: [
        { text: 'Chưa làm', value: 'PENDING' },
        { text: 'Quá hạn', value: 'OVERDUE' },
        { text: 'Đã nạp đáo', value: 'PAID' },
        { text: 'Đã hoàn thành', value: 'COMPLETED' },
      ],
      onFilter: (value: any, record: EnrichedMaturitySchedule) =>
        record.status === value,
      filteredValue: filteredInfo.status || null,
    },
  ];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Chỉ thêm những record trong filtered data mà chưa được chọn
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = Array.from(
        new Set([...selectedRowKeys, ...currentIds])
      );
      setSelectedRowKeys(newSelections);
    } else {
      // Chỉ bỏ chọn những record trong filtered data, giữ lại những record khác
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = selectedRowKeys.filter(
        (id) => !currentIds.includes(id)
      );
      setSelectedRowKeys(newSelections);
    }
  };

  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một kỳ đáo hạn để xuất Excel');
      return;
    }

    try {
      setLoading(true);
      const selectedRecords = validMaturitySchedules.filter((record) =>
        selectedRowKeys.includes(record.id)
      );

      // Pass enriched data with customer details to Excel export
      await exportMultipleMaturitySchedulesToExcel({
        records: selectedRecords,
        month: selectedMonth,
        year: selectedYear,
      });

      message.success(
        `Đã xuất Excel thành công cho ${selectedRecords.length} kỳ đáo hạn`
      );
      onClose();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedRowKeys([]);
    setSearchedColumn('');
    setEnrichedData([]);
    // Reset table key to clear all filters when modal closes
    setTableKey((prev) => prev + 1);
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <FileExcelOutlined className="text-green-600" />
          <span>Xuất Excel nhiều kỳ đáo hạn</span>
        </div>
      }
      open={visible}
      onCancel={handleModalClose}
      width={1600}
      destroyOnClose={true}
      footer={[
        <Button key="cancel" onClick={handleModalClose}>
          Hủy
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={loading}
          disabled={selectedRowKeys.length === 0}
        >
          Xuất Excel ({selectedRowKeys.length} kỳ đã chọn)
        </Button>,
      ]}
      className="multi-export-modal"
    >
      <div className="space-y-4">
        {/* Period Info */}
        <Card size="small" className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <Text strong>Kỳ xuất báo cáo: </Text>
              <Tag color="blue" className="text-base">
                Tháng {selectedMonth}/{selectedYear}
              </Tag>
            </div>
            <div>
              <Text type="secondary">
                Tổng cộng: {validMaturitySchedules.length} kỳ đáo hạn
              </Text>
            </div>
          </div>
        </Card>

        {/* Search and controls */}
        <div className="flex items-center justify-between">
          <Space>
            <Text type="secondary" className="text-xs">
              💡 Mẹo: Click icon tìm kiếm ở header cột để lọc chi tiết
            </Text>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearAllFilters}
              type="default"
              size="small"
              className="text-red-500 hover:text-red-700 hover:border-red-300 border-red-200"
              title="Xóa tất cả bộ lọc"
            >
              <span className="text-xs">Xóa tất cả bộ lọc</span>
            </Button>
            {loadingCustomers && (
              <Text type="secondary" className="text-xs">
                🔄 Đang tải thông tin CTV...
              </Text>
            )}
          </Space>
          <Text type="secondary">
            Hiển thị: {currentFilteredData.length}/
            {validMaturitySchedules.length} kỳ đáo hạn
          </Text>
        </div>

        {/* Selection Controls */}
        <div className="flex items-center justify-between">
          <Space>
            <Checkbox
              checked={
                currentFilteredData.length > 0 &&
                currentFilteredData.every((record) =>
                  selectedRowKeys.includes(record.id)
                )
              }
              indeterminate={
                selectedRowKeys.length > 0 &&
                currentFilteredData.some((record) =>
                  selectedRowKeys.includes(record.id)
                ) &&
                !currentFilteredData.every((record) =>
                  selectedRowKeys.includes(record.id)
                )
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Chọn hiển thị
            </Checkbox>
            <Text type="secondary">
              Đã chọn: {selectedRowKeys.length} kỳ | Hiển thị:{' '}
              {
                currentFilteredData.filter((record) =>
                  selectedRowKeys.includes(record.id)
                ).length
              }
              /{currentFilteredData.length}
            </Text>
          </Space>
          {Object.keys(filteredInfo).length > 0 && (
            <Tag color="orange" className="text-sm">
              Đang áp dụng bộ lọc
            </Tag>
          )}
        </div>

        {/* Table */}
        <Table
          ref={tableRef}
          columns={columns}
          dataSource={validMaturitySchedules}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} kỳ đáo hạn`,
          }}
          scroll={{ y: 400, x: 1500 }}
          size="small"
          className="border border-gray-200"
          loading={loadingCustomers}
          key={tableKey}
          onChange={handleTableChange}
        />

        {/* Preview Summary */}
        {selectedRowKeys.length > 0 && (
          <>
            <Divider />
            <Card
              size="small"
              title={
                <div className="flex items-center space-x-2">
                  <EyeOutlined />
                  <span>Xem trước tổng hợp</span>
                </div>
              }
              className="bg-green-50 border-green-200"
            >
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <Text type="secondary">Số kỳ đáo hạn:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {previewData.count} kỳ
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng tiền đáo hạn:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {formatCurrency(previewData.totalRepayment)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng đã nạp:</Text>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(previewData.totalDeposit)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng đã rút:</Text>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatCurrency(previewData.totalWithdrawal)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalFee)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí CTV:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalCtvCommission)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí HH:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalMargin)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Chênh lệch:</Text>
                  <div
                    className={`text-lg font-semibold ${previewData.totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(previewData.totalBalance)}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MultiExportModal;
